import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  ArrowRight, CheckCircle, Shield, GraduationCap, Brain,
  RefreshCw, Target, Users, Briefcase, TrendingUp,
  Calendar, MapPin, FileText,
  Sparkles, Zap, Gift, Clock, FileCheck, Mail,
  BarChart3, Building, Star, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Event } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import AnimatedSection from '../components/ui/AnimatedSection';
import { useAuth } from '../contexts/AuthContext';
import { useViewMode } from '../contexts/ViewModeContext';
import apiService from '../services/api';

/* ─── Animation variants ─────────────────────────────────────── */
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const stagger: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

/* ─── Hero image mockup ──────────────────────────────────────── */
const JobCardMockup: React.FC = () => (
  <motion.div
    animate={{ y: [0, -10, 0] }}
    transition={{ duration: 4, ease: 'easeInOut', repeat: Infinity }}
    className="w-full mx-auto relative"
  >
    <img
      src="https://linzara.com/hero-home.jpg"
      alt="Linzara – visa-aware job matching"
      className="w-full h-auto rounded-2xl shadow-float border border-slate-200"
    />
  </motion.div>
);

/* ─── Employer hero image ────────────────────────────────────── */
const EmployerHeroImage: React.FC = () => (
  <motion.div
    animate={{ y: [0, -10, 0] }}
    transition={{ duration: 4.5, ease: 'easeInOut', repeat: Infinity, delay: 0.5 }}
    className="w-full mx-auto relative"
  >
    <img
      src="https://images.unsplash.com/photo-1573497701240-345a300b8d36?w=700&auto=format&fit=crop&q=80"
      alt="Employer hiring with confidence"
      className="w-full h-auto rounded-2xl shadow-float border border-slate-200 object-cover"
    />
  </motion.div>
);


/* ─── Feature card ───────────────────────────────────────────── */
interface Feature {
  icon: React.ReactNode;
  title: string;
  desc: string;
  items: string[];
  iconBg: string;
  iconColor: string;
}
const FeatureCard: React.FC<Feature> = ({ icon, title, desc, items, iconBg, iconColor }) => (
  <motion.div
    whileHover={{ y: -4 }}
    transition={{ type: 'spring', stiffness: 300, damping: 22 }}
    className="card p-6 flex flex-col gap-4 group hover:border-primary-100 transition-colors duration-300 h-full"
  >
    <div className={`feature-icon ${iconBg}`}>
      <span className={iconColor}>{icon}</span>
    </div>
    <div>
      <h3 className="text-base font-bold text-slate-900 mb-1">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
    <ul className="space-y-1.5 mt-auto">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
          <CheckCircle className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </motion.div>
);

/* ─── Testimonial ────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    quote: "I spent 7 days perfecting my application for a law firm's advertised job, just to get rejected because they don't hire international students. Your platform will have students like me save invaluable time.",
    author: 'UoN Law Student',
    role: 'Pilot Job Seeker',
    color: 'from-teal-50',
  },
  {
    quote: "Too many skilled visa holders end up in roles like rideshare driving. Linzara helps unlock their potential by guiding them into meaningful jobs and connecting employers with motivated, visa-compliant talent.",
    author: 'Prof. Chennupati Jagadish',
    role: 'President, Australian Academy of Science',
    color: 'from-blue-50',
  },
  {
    quote: "Finding meaningful, visa-compliant work is a real challenge for international students. Linzara directly addresses this need — it is an innovative solution with real impact for both students and businesses.",
    author: 'Prof. Alex Zelinsky AO',
    role: 'President & VC, University of Newcastle',
    color: 'from-purple-50',
  },
];

/* ─── Main component ─────────────────────────────────────────── */
const HomePage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { viewMode, setViewMode } = useViewMode();

  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const touchStartX = React.useRef<number | null>(null);
  const [latestBlogs, setLatestBlogs] = useState<any[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      setTestimonialIdx(p => (p + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [autoPlay]);

  useEffect(() => {
    apiService.getAllBlogs().then(b => setLatestBlogs(b.slice(0, 3))).catch(() => {});
    apiService.getAllEvents().then(ev => {
      const now = new Date();
      setUpcomingEvents(ev.filter(e => new Date(e.event_date) >= now).slice(0, 3));
    }).catch(() => {});
  }, []);

  const pauseAuto = () => {
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const prevTestimonial = () => {
    pauseAuto();
    setTestimonialIdx(p => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const nextTestimonial = () => {
    pauseAuto();
    setTestimonialIdx(p => (p + 1) % TESTIMONIALS.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    pauseAuto();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? nextTestimonial() : prevTestimonial();
    }
    touchStartX.current = null;
  };

  const candidateFeatures: Feature[] = [
    {
      icon: <Shield className="h-6 w-6" />, iconBg: 'bg-teal-50', iconColor: 'text-teal-600',
      title: 'Visa-Friendly Jobs',
      desc: 'Curated opportunities from accredited sponsors who can support your visa application.',
      items: ['Student Visa (subclass 500)', 'Temporary Graduate (485)', 'Employer Sponsored (482)'],
    },
    {
      icon: <GraduationCap className="h-6 w-6" />, iconBg: 'bg-green-50', iconColor: 'text-green-600',
      title: 'Student-Friendly Positions',
      desc: 'Part-time and casual roles that comply with student visa work restrictions.',
      items: ['48 hours per fortnight limit', 'Holiday work rights', 'Course-related experience'],
    },
    {
      icon: <Brain className="h-6 w-6" />, iconBg: 'bg-purple-50', iconColor: 'text-purple-600',
      title: 'AI-Powered Matching',
      desc: 'Smart algorithms match your skills and visa status with the right opportunities.',
      items: ['Skill assessment', 'Visa compatibility', 'Career progression'],
    },
    {
      icon: <RefreshCw className="h-6 w-6" />, iconBg: 'bg-orange-50', iconColor: 'text-orange-600',
      title: 'Real-Time Updates',
      desc: 'Fresh job listings updated daily from trusted sources and company career pages.',
      items: ['Daily updates', 'Verified employers', 'Direct applications'],
    },
    {
      icon: <Target className="h-6 w-6" />, iconBg: 'bg-red-50', iconColor: 'text-red-600',
      title: 'Filtered to Your Visa',
      desc: 'Jobs filtered to your visa type — no guesswork, no wasted applications.',
      items: ['Only eligible jobs', 'Visa-aware matching', 'No more rejections'],
    },
    {
      icon: <Shield className="h-6 w-6" />, iconBg: 'bg-primary-50', iconColor: 'text-primary-600',
      title: 'Visa-Aware Tools',
      desc: 'Integrated VEVO document upload and declared visa context to help you find roles aligned to your situation.',
      items: ['VEVO document upload', 'Work eligibility context', 'Visa-aware matching'],
    },
  ];

  const employerFeatures: Feature[] = [
    {
      icon: <Target className="h-6 w-6" />, iconBg: 'bg-blue-50', iconColor: 'text-blue-600',
      title: 'Visa Readiness Check',
      desc: 'Know exactly who you can hire in 60 seconds — visa types, work-hour caps, sponsorship.',
      items: ['60-second wizard', 'Official Home Affairs links', 'Compliance notes'],
    },
    {
      icon: <FileCheck className="h-6 w-6" />, iconBg: 'bg-green-50', iconColor: 'text-green-600',
      title: 'Guided Job Posting',
      desc: 'Post visa-aware jobs in 2 minutes with structured guidance built in.',
      items: ['2-minute posting', 'Visa-aware guidance', 'Fair Work context'],
    },
    {
      icon: <Mail className="h-6 w-6" />, iconBg: 'bg-purple-50', iconColor: 'text-purple-600',
      title: 'Concierge Service',
      desc: 'Email your JD and we handle the rest — visa-aware postings, candidate briefs, shortlists.',
      items: ['Email-based workflow', 'Hands-off process', 'Structured guidance'],
    },
    {
      icon: <Users className="h-6 w-6" />, iconBg: 'bg-orange-50', iconColor: 'text-orange-600',
      title: 'Pre-Vetted Talent',
      desc: 'Stop sorting 200 CVs. Get top 3–5 candidates with declared work eligibility and matched skills.',
      items: ['VEVO document upload', 'Smart matching', 'Shortlist packs'],
    },
    {
      icon: <Shield className="h-6 w-6" />, iconBg: 'bg-red-50', iconColor: 'text-red-600',
      title: 'Visa-Aware Screening',
      desc: 'Built-in context on student visa conditions, WHV typical limits, and sponsorship visibility.',
      items: ['Declared visa context', 'WHV awareness', 'Sponsorship clarity'],
    },
    {
      icon: <BarChart3 className="h-6 w-6" />, iconBg: 'bg-teal-50', iconColor: 'text-teal-600',
      title: 'Employer Dashboard',
      desc: 'Candidate briefs, active roles, shortlists, and hiring activity in one place.',
      items: ['Single dashboard', 'Minimal clicks', 'Maximum clarity'],
    },
  ];

  return (
    <div className="overflow-x-hidden">

      <AnimatePresence mode="sync" initial={false}>

        {/* ═══════════════ AGNOSTIC / PICKER VIEW ═══════════════ */}
        {viewMode === 'agnostic' && (
          <motion.div
            key="agnostic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <section className="relative overflow-hidden min-h-[90vh] flex flex-col items-center justify-center py-10 sm:py-14"
              style={{ background: 'linear-gradient(160deg, #f0fdfa 0%, #ffffff 50%, #eff6ff 100%)' }}>
              {/* Grid mesh */}
              <div
                className="absolute inset-0 opacity-[0.35]"
                style={{ backgroundImage: 'linear-gradient(to right, #0d948828 1px, transparent 1px), linear-gradient(to bottom, #0d948828 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              <div className="orb w-[600px] h-[600px] -top-40 -right-40 bg-primary-200 opacity-25" />
              <div className="orb w-[500px] h-[500px] -bottom-32 -left-32 bg-blue-200 opacity-20" />

              <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <motion.div
                  variants={stagger} initial="hidden" animate="show"
                  className="text-center mb-14"
                >
                  <motion.h1 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                    Visa-aware employment<br />
                    <span className="relative inline-block">
                      <span className="gradient-text">for Australia's diverse workforce.</span>
                      <span className="absolute bottom-0.5 left-0 right-0 h-[6px] bg-primary-200/50 -z-10 rounded-sm skew-x-[-2deg]" />
                    </span>
                  </motion.h1>
                  <motion.p variants={fadeUp} className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mt-5">
                    Linzara helps international students and other visa holders build clearer career pathways — while helping employers and partners connect with skilled, diverse, work-ready talent.
                  </motion.p>
                  <motion.p variants={fadeUp} className="text-sm font-semibold text-slate-400 mt-8 tracking-wide uppercase">
                    Choose your pathway
                  </motion.p>
                </motion.div>

                {/* Picker cards */}
                <motion.div
                  variants={stagger} initial="hidden" animate="show"
                  className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto"
                >
                  {/* Candidate card */}
                  <motion.button
                    variants={fadeUp}
                    whileHover={{ y: -6, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                    onClick={() => setViewMode('candidate')}
                    className="group text-left card p-8 flex flex-col gap-5 border-2 border-slate-100 hover:border-primary-300 hover:shadow-lg transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center text-teal-600 flex-shrink-0 group-hover:bg-teal-100 transition-colors">
                        <GraduationCap className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-0.5">For candidates</p>
                        <h2 className="text-xl font-extrabold text-slate-900">I'm looking for work</h2>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Find visa-aware opportunities, understand your role fit, and get guidance to strengthen your profile.
                    </p>
                    <ul className="space-y-2">
                      {[
                        'Opportunities aligned to your declared visa type',
                        'Role-fit and skills analysis',
                        'Career guidance and role insights',
                      ].map(item => (
                        <li key={item} className="flex items-center gap-2 text-xs text-slate-600">
                          <CheckCircle className="h-3.5 w-3.5 text-teal-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex items-center gap-2 text-sm font-bold text-primary-600 group-hover:text-primary-700">
                      Get started <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </motion.button>

                  {/* Employer card */}
                  <motion.button
                    variants={fadeUp}
                    whileHover={{ y: -6, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                    onClick={() => setViewMode('employer')}
                    className="group text-left card p-8 flex flex-col gap-5 border-2 border-slate-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                        <Building className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-0.5">For employers</p>
                        <h2 className="text-xl font-extrabold text-slate-900">I'm hiring</h2>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Access skilled, diverse, work-ready candidates with clearer profiles and better visibility around declared work eligibility.
                    </p>
                    <ul className="space-y-2">
                      {[
                        'Pre-screened candidate profiles',
                        'Work-rights visibility',
                        'Smarter shortlisting support',
                      ].map(item => (
                        <li key={item} className="flex items-center gap-2 text-xs text-slate-600">
                          <CheckCircle className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex items-center gap-2 text-sm font-bold text-blue-600 group-hover:text-blue-700">
                      Start hiring <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </motion.button>
                </motion.div>

              </div>
            </section>
          </motion.div>
        )}

        {/* ═══════════════ CANDIDATE VIEW ═══════════════════════ */}
        {viewMode === 'candidate' && (
          <motion.div
            key="candidate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.25 }}
          >

            {/* ── Hero ── */}
            <section className="relative overflow-hidden pt-14 pb-28 sm:pt-32 sm:pb-36"
              style={{ background: 'linear-gradient(160deg, #f0fdfa 0%, #ffffff 45%, #f8fafc 100%)' }}>
              {/* Subtle grid mesh */}
              <div
                className="absolute inset-0 opacity-[0.4]"
                style={{ backgroundImage: 'linear-gradient(to right, #0d948828 1px, transparent 1px), linear-gradient(to bottom, #0d948828 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              {/* Stronger orbs */}
              <div className="orb w-[700px] h-[700px] -top-48 -right-48 bg-primary-200 opacity-30" />
              <div className="orb w-[500px] h-[500px] bottom-0 -left-32 bg-cyan-200 opacity-25" />
              <div className="orb w-[300px] h-[300px] top-1/3 left-1/4 bg-teal-300 opacity-15" />

              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">

                  {/* Left */}
                  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
                    <motion.h1 variants={fadeUp} className="text-[2.6rem] sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight">
                      Visa-aware employment<br />
                      <span className="relative inline-block">
                        <span className="gradient-text">and career guidance</span>
                        <span className="absolute bottom-0.5 left-0 right-0 h-[8px] bg-primary-200/50 -z-10 rounded-sm skew-x-[-2deg]" />
                      </span>{' '}
                      <span className="gradient-text">for Australia.</span>
                    </motion.h1>

                    <motion.p variants={fadeUp} className="text-lg text-slate-600 leading-relaxed max-w-lg mt-1">
                      Linzara helps international students and visa holders access clearer employment pathways and career guidance — while helping employers and partners connect with skilled, diverse, work-ready talent.
                    </motion.p>

                    <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
                      <span className="pill-green">
                        <CheckCircle className="h-3 w-3" /> Visa-Aware
                      </span>
                      <span className="pill-blue">
                        <Brain className="h-3 w-3" /> AI Matching
                      </span>
                      <span className="pill-purple">
                        <GraduationCap className="h-3 w-3" /> Work-Ready Talent
                      </span>
                    </motion.div>

                    <motion.div variants={fadeUp} className="flex flex-col sm:flex-row flex-wrap gap-3 pt-1">
                      {!isAuthenticated && (
                        <Link to="/early-access">
                          <Button size="lg" iconRight={<ArrowRight className="h-4 w-4" />}>
                            Join Beta Waitlist
                          </Button>
                        </Link>
                      )}
                      {isAuthenticated && (
                        <Link to="/career/assistant">
                          <Button size="lg" variant="outline">
                            <Sparkles className="h-4 w-4 mr-2" /> Career Coach
                          </Button>
                        </Link>
                      )}
                    </motion.div>

                  </motion.div>

                  {/* Right — floating mockup */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25, ease: 'easeOut' }}
                    className="relative flex justify-center"
                  >
                    <JobCardMockup />
                  </motion.div>
                </div>
              </div>
            </section>


            {/* ── How It Works ── */}
            <section className="py-16 sm:py-20 bg-white">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-16">
                  <span className="section-eyebrow mb-5">Simple process</span>
                  <h2 className="section-heading mt-3">How Linzara works for candidates</h2>
                  <p className="section-subtext mx-auto mt-4 max-w-xl">Three steps to finding work that fits your rights and your goals.</p>
                </AnimatedSection>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                  {[
                    { step: '01', title: 'Build your profile', desc: 'Add your visa details and skills. We surface them clearly so employers can see your situation at a glance.' },
                    { step: '02', title: 'See your fit',       desc: 'View roles and understand your chances based on your declared visa type, skills, experience and education.' },
                    { step: '03', title: 'Apply with confidence', desc: 'Your profile is visa-compliant and employer-ready. Apply knowing every role fits your rights.' },
                  ].map((item, i) => (
                    <AnimatedSection key={i} delay={i * 120} direction="up" className="h-full">
                      <div className="relative flex flex-col items-center text-center p-8 h-full rounded-2xl bg-gradient-to-b from-white to-slate-50 border border-slate-200 hover:border-primary-200 hover:shadow-card-hover transition-all duration-300 group">
                        <div className="step-badge mb-5 w-12 h-12 text-base shadow-glow-sm">{item.step}</div>
                        <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors">{item.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                        {i < 2 && (
                          <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-20 bg-white rounded-full p-1 shadow-sm border border-slate-100">
                            <ArrowRight className="h-4 w-4 text-slate-300" />
                          </div>
                        )}
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Beta Waitlist ── */}
            <section className="py-20 sm:py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-slate-900 relative overflow-hidden">
              <div className="orb w-96 h-96 top-0 left-0 bg-primary-400 opacity-20" />
              <div className="orb w-72 h-72 bottom-0 right-0 bg-cyan-400 opacity-15" />
              <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
                <AnimatedSection>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-white text-xs font-semibold mb-6">
                    <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                    BETA WAITLIST OPEN
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-tight">
                    🎉 Join the Beta Waitlist
                  </h2>
                  <p className="text-white/80 text-base mb-10 max-w-xl mx-auto">
                    Sign up now and we'll email you when Founding Beta applications open
                  </p>
                </AnimatedSection>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 items-stretch">
                  {[
                    { icon: <Gift className="h-5 w-5" />, title: 'Founding Beta Access', desc: 'Unlock Deep Analysis reports and Career Coach once your application is approved.' },
                    { icon: <Zap  className="h-5 w-5" />, title: 'Priority Matching', desc: 'Your profile gets shown first to employers actively hiring.' },
                    { icon: <Clock className="h-5 w-5" />, title: 'Early Access', desc: 'Be the first to try new features and shape the platform\'s future.' },
                  ].map(({ icon, title, desc }) => (
                    <AnimatedSection key={title} direction="scale" className="h-full">
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 sm:p-6 h-full border border-white/20 hover:bg-white/15 transition-colors text-left">
                        <div className="bg-white/20 w-10 h-10 rounded-xl flex items-center justify-center text-white mb-4">{icon}</div>
                        <h3 className="text-sm font-bold text-white mb-2">{title}</h3>
                        <p className="text-white/70 text-sm leading-relaxed">{desc}</p>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
                <Link to={isAuthenticated ? '/profile' : '/early-access'}>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary-700 font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-shadow text-base"
                  >
                    <Sparkles className="h-5 w-5" />
                    {isAuthenticated ? 'View Your Status' : 'Join Beta Waitlist'}
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
                <p className="text-white/70 text-xs mt-4">No account or payment required to join the waitlist</p>
              </div>
            </section>

            {/* ── Features ── */}
            <section className="py-16 sm:py-20 bg-slate-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-14">
                  <span className="section-eyebrow mb-5">Why Linzara</span>
                  <h2 className="section-heading mt-3">Built for international workers</h2>
                  <p className="section-subtext mx-auto mt-4 max-w-xl">
                    Not just another job board — every feature is designed around the complexity of working in Australia as a visa holder.
                  </p>
                </AnimatedSection>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
                  {candidateFeatures.map((f, i) => (
                    <AnimatedSection key={i} delay={i * 80} direction="up" className="h-full">
                      <FeatureCard {...f} />
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Testimonials ── */}
            <section className="py-16 sm:py-20 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-12">
                  <span className="section-eyebrow mb-5">Social proof</span>
                  <h2 className="section-heading mt-3">Trusted by candidates, leaders &amp; employers</h2>
                  <p className="section-subtext mx-auto mt-4">See what people are saying about Linzara.</p>
                </AnimatedSection>

                <div className="hidden md:grid md:grid-cols-3 gap-6">
                  {TESTIMONIALS.map((t, i) => (
                    <AnimatedSection key={i} delay={i * 100} direction="up">
                      <div className={`card p-6 flex flex-col gap-4 bg-gradient-to-br ${t.color} to-white h-full`}>
                        <div className="flex gap-0.5 mb-1">
                          {[...Array(5)].map((_, si) => <Star key={si} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed italic flex-1">"{t.quote}"</p>
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-sm font-bold text-slate-900">{t.author}</p>
                          <p className="text-xs text-slate-500">{t.role}</p>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>

                <div className="md:hidden relative">
                  <div
                    className="overflow-hidden"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      className="flex transition-transform duration-500 ease-out"
                      style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}
                    >
                      {TESTIMONIALS.map((t, i) => (
                        <div key={i} className="min-w-full px-1">
                          <div className={`card p-6 flex flex-col gap-4 bg-gradient-to-br ${t.color} to-white`}>
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, si) => <Star key={si} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed italic">"{t.quote}"</p>
                            <div className="pt-3 border-t border-slate-100">
                              <p className="text-sm font-bold text-slate-900">{t.author}</p>
                              <p className="text-xs text-slate-500">{t.role}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-5">
                    <button onClick={prevTestimonial} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                      <ChevronLeft className="h-4 w-4 text-slate-600" />
                    </button>
                    <div className="flex gap-1.5">
                      {TESTIMONIALS.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => { setTestimonialIdx(i); pauseAuto(); }}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            testimonialIdx === i ? 'w-6 bg-primary-600' : 'w-1.5 bg-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <button onClick={nextTestimonial} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                      <ChevronRight className="h-4 w-4 text-slate-600" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Events preview ── */}
            {upcomingEvents.length > 0 && (
              <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <AnimatedSection className="text-center mb-12">
                    <h2 className="section-heading">Upcoming Events</h2>
                    <p className="section-subtext mx-auto mt-4">Workshops, talks, and networking for international talent and employers.</p>
                  </AnimatedSection>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {upcomingEvents.map((event, i) => (
                      <AnimatedSection key={event.id} delay={i * 100}>
                        <Link to={`/events/${event.id}`}>
                          <Card hover className="h-full flex flex-col p-5">
                            <div className="flex items-center gap-2 mb-3">
                              {event.is_featured && <span className="pill-amber">Featured</span>}
                              <span className="pill-green">Upcoming</span>
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">{event.title}</h3>
                            <p className="text-xs text-slate-500 line-clamp-2 flex-1 mb-4">{event.description.substring(0, 100)}…</p>
                            <div className="space-y-1.5 text-xs text-slate-500 border-t border-slate-100 pt-3">
                              <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-primary-500" />{new Date(event.event_date).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                              <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary-500" />{event.is_online ? 'Online' : event.location}</div>
                            </div>
                          </Card>
                        </Link>
                      </AnimatedSection>
                    ))}
                  </div>
                  <div className="text-center">
                    <Link to="/events"><Button variant="outline" iconRight={<ArrowRight className="h-4 w-4" />}>View All Events</Button></Link>
                  </div>
                </div>
              </section>
            )}

            {/* ── Blog preview ── */}
            {latestBlogs.length > 0 && (
              <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <AnimatedSection className="text-center mb-12">
                    <h2 className="section-heading">Latest from Our Blog</h2>
                    <p className="section-subtext mx-auto mt-4">Tips, insights, and news for international students and job seekers.</p>
                  </AnimatedSection>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {latestBlogs.map((blog, i) => (
                      <AnimatedSection key={blog.id} delay={i * 100}>
                        <Link to={`/blog/${blog.id}`}>
                          <Card hover className="h-full flex flex-col p-5">
                            <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
                              <FileText className="h-3.5 w-3.5" /> Blog Post
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">{blog.title}</h3>
                            <p className="text-xs text-slate-500 line-clamp-3 flex-1 mb-4">{blog.content.substring(0, 140)}…</p>
                            <div className="flex items-center gap-1.5 text-xs text-slate-400 border-t border-slate-100 pt-3">
                              <Calendar className="h-3.5 w-3.5" />
                              {new Date(blog.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                          </Card>
                        </Link>
                      </AnimatedSection>
                    ))}
                  </div>
                  <div className="text-center">
                    <Link to="/blog"><Button variant="outline" iconRight={<ArrowRight className="h-4 w-4" />}>View All Posts</Button></Link>
                  </div>
                </div>
              </section>
            )}

            {/* ── CTA ── */}
            <section className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #0891b2 100%)' }}>
              <div className="orb w-96 h-96 -top-20 -right-20 bg-white opacity-5" />
              <div className="orb w-64 h-64 bottom-0 left-0 bg-white opacity-5" />
              <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              <div className="relative max-w-3xl mx-auto px-4 text-center">
                <AnimatedSection>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/25 text-white text-sm font-semibold mb-6">
                    <Sparkles className="h-4 w-4" /> Free to join · No credit card
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight">
                    Ready to start your<br />Australian career journey?
                  </h2>
                  <p className="text-white text-lg mb-10 max-w-lg mx-auto">
                    Join thousands of international professionals who've found their path through Linzara.
                  </p>
                  {!isAuthenticated && (
                    <div className="flex justify-center">
                      <Link to="/auth?tab=register&role=student">
                        <motion.button
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-shadow text-base"
                        >
                          <ArrowRight className="h-5 w-5" /> Create Your Profile
                        </motion.button>
                      </Link>
                    </div>
                  )}
                </AnimatedSection>
              </div>
            </section>

          </motion.div>
        )}

        {/* ═══════════════ EMPLOYER VIEW ════════════════════════ */}
        {viewMode === 'employer' && (
          <motion.div
            key="employer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.25 }}
          >

            {/* ── Hero ── */}
            <section className="relative overflow-hidden pt-14 pb-28 sm:pt-32 sm:pb-36"
              style={{ background: 'linear-gradient(160deg, #eff6ff 0%, #ffffff 40%, #f8fafc 100%)' }}>
              <div
                className="absolute inset-0 opacity-[0.3]"
                style={{ backgroundImage: 'linear-gradient(to right, #3b82f620 1px, transparent 1px), linear-gradient(to bottom, #3b82f620 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              <div className="orb w-[700px] h-[700px] -top-48 -right-48 bg-blue-200 opacity-30" />
              <div className="orb w-[500px] h-[500px] bottom-0 -left-32 bg-primary-200 opacity-25" />

              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">

                  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
                    <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.08] tracking-tight">
                      Hire confidently.<br />
                      <span className="gradient-text">Compliance, built in.</span>
                    </motion.h1>

                    <motion.p variants={fadeUp} className="text-lg text-slate-600 leading-relaxed max-w-lg">
                      Candidates with declared work eligibility, matched to your role. Post a job in 2 minutes.
                      No more guessing about visa rules — just compliant hiring made simple.
                    </motion.p>

                    <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
                      <span className="pill-green">
                        <CheckCircle className="h-3 w-3" /> Compliance Built-In
                      </span>
                      <span className="pill-blue">
                        <Users className="h-3 w-3" /> Pre-Vetted Talent
                      </span>
                      <span className="pill-amber">
                        <Zap className="h-3 w-3" /> 2-Minute Posting
                      </span>
                    </motion.div>

                    <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 pt-1">
                      <Link to="/request-demo">
                        <Button size="lg" iconRight={<ArrowRight className="h-4 w-4" />}>
                          Get Early Access
                        </Button>
                      </Link>
                    </motion.div>

                    <motion.p variants={fadeUp} className="text-sm text-slate-500">
                      Trusted by SMEs, startups, and enterprise teams across Australia.
                    </motion.p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25, ease: 'easeOut' }}
                    className="relative flex justify-center"
                  >
                    <EmployerHeroImage />
                  </motion.div>
                </div>
              </div>
            </section>

            {/* ── How it works (employer) ── */}
            <section className="py-16 sm:py-20 bg-white">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-16">
                  <span className="section-eyebrow mb-5">Simple process</span>
                  <h2 className="section-heading mt-3">How Linzara works for employers</h2>
                  <p className="section-subtext mx-auto mt-4">Three steps to hiring the right people with full clarity.</p>
                </AnimatedSection>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { step: '01', title: 'Pre-filtered candidates', desc: 'We match and filter candidates based on declared visa type, conditions, and role requirements.' },
                    { step: '02', title: 'Review with clarity',      desc: 'See profiles where eligibility, availability, and duration are already understood.' },
                    { step: '03', title: 'Hire with confidence',     desc: 'Make faster decisions with less uncertainty around visa eligibility.' },
                  ].map((item, i) => (
                    <AnimatedSection key={i} delay={i * 120}>
                      <div className="relative flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-b from-white to-slate-50 border border-slate-200 hover:border-primary-200 hover:shadow-card-hover transition-all duration-300 group">
                        <div className="step-badge mb-5 w-12 h-12 text-base shadow-glow-sm">{item.step}</div>
                        <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors">{item.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                        {i < 2 && (
                          <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-20 bg-white rounded-full p-1 shadow-sm border border-slate-100">
                            <ArrowRight className="h-4 w-4 text-slate-300" />
                          </div>
                        )}
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Employer features ── */}
            <section className="py-20 sm:py-28 bg-slate-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-14">
                  <span className="section-eyebrow mb-5">Why Linzara</span>
                  <h2 className="section-heading mt-3">Everything you need to hire right</h2>
                  <p className="section-subtext mx-auto mt-4 max-w-xl">
                    Not just a job board — a visa-aware hiring platform built to reduce uncertainty around Australia's diverse workforce.
                  </p>
                </AnimatedSection>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
                  {employerFeatures.map((f, i) => (
                    <AnimatedSection key={i} delay={i * 80} direction="up" className="h-full">
                      <FeatureCard {...f} />
                    </AnimatedSection>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Testimonials ── */}
            <section className="py-16 sm:py-20 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection className="text-center mb-12">
                  <span className="section-eyebrow mb-5">Social proof</span>
                  <h2 className="section-heading mt-3">Trusted by candidates, leaders &amp; employers</h2>
                  <p className="section-subtext mx-auto mt-4">See what people are saying about Linzara.</p>
                </AnimatedSection>

                <div className="hidden md:grid md:grid-cols-3 gap-6">
                  {TESTIMONIALS.map((t, i) => (
                    <AnimatedSection key={i} delay={i * 100} direction="up">
                      <div className={`card p-6 flex flex-col gap-4 bg-gradient-to-br ${t.color} to-white h-full`}>
                        <div className="flex gap-0.5 mb-1">
                          {[...Array(5)].map((_, si) => <Star key={si} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed italic flex-1">"{t.quote}"</p>
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-sm font-bold text-slate-900">{t.author}</p>
                          <p className="text-xs text-slate-500">{t.role}</p>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>

                <div className="md:hidden relative">
                  <div
                    className="overflow-hidden"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      className="flex transition-transform duration-500 ease-out"
                      style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}
                    >
                      {TESTIMONIALS.map((t, i) => (
                        <div key={i} className="min-w-full px-1">
                          <div className={`card p-6 flex flex-col gap-4 bg-gradient-to-br ${t.color} to-white`}>
                            <div className="flex gap-0.5">
                              {[...Array(5)].map((_, si) => <Star key={si} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed italic">"{t.quote}"</p>
                            <div className="pt-3 border-t border-slate-100">
                              <p className="text-sm font-bold text-slate-900">{t.author}</p>
                              <p className="text-xs text-slate-500">{t.role}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-5">
                    <button onClick={prevTestimonial} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                      <ChevronLeft className="h-4 w-4 text-slate-600" />
                    </button>
                    <div className="flex gap-1.5">
                    {TESTIMONIALS.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => { setTestimonialIdx(i); pauseAuto(); }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          testimonialIdx === i ? 'w-6 bg-primary-600' : 'w-1.5 bg-slate-300'
                        }`}
                      />
                    ))}
                    </div>
                    <button onClick={nextTestimonial} className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                      <ChevronRight className="h-4 w-4 text-slate-600" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Employer CTA ── */}
            <section className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 50%, #0d9488 100%)' }}>
              <div className="orb w-96 h-96 -bottom-20 -right-20 bg-primary-400 opacity-15" />
              <div
                className="absolute inset-0 opacity-10"
                style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
              />
              <div className="relative max-w-3xl mx-auto px-4 text-center">
                <AnimatedSection>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/25 text-white text-sm font-semibold mb-6">
                    <Building className="h-4 w-4" /> For Employers · Beta Access
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight">
                    Ready to hire smarter?
                  </h2>
                  <p className="text-white text-lg mb-10 max-w-lg mx-auto">
                    Be among the first employers to simplify visa-aware hiring on Linzara.
                  </p>
                  <Link to="/request-demo">
                    <motion.button
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-white text-slate-800 font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-shadow text-base"
                    >
                      <Target className="h-5 w-5 text-primary-600" />
                      Request Demo
                      <ArrowRight className="h-5 w-5" />
                    </motion.button>
                  </Link>
                </AnimatedSection>
              </div>
            </section>

          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default HomePage;
